/**
 * Wholesale Pricing API Client
 * Phase 2: Wholesale Partnership Module
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/pricing';

// ===== PRICE TIERS =====

/**
 * Get all price tiers
 */
export const getPriceTiers = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/tiers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching price tiers:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch price tiers',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get single price tier by ID
 */
export const getTierById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/tiers/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching price tier:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch price tier',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update price tier configuration
 */
export const updatePriceTier = async (id, data) => {
  try {
    const response = await apiClient.put(`${BASE_URL}/tiers/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating price tier:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update price tier',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== PRICE CALCULATION =====

/**
 * Calculate price for a product
 */
export const calculateProductPrice = async (productId, distributorId, quantity) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/calculate`, {
      product_id: productId,
      distributor_id: distributorId,
      quantity
    });
    return response.data;
  } catch (error) {
    console.error('Error calculating product price:', error);
    throw {
      message: error.response?.data?.error || 'Failed to calculate product price',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Calculate prices for an entire order
 */
export const calculateOrderPrices = async (distributorId, items) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/calculate-order`, {
      distributor_id: distributorId,
      items
    });
    return response.data;
  } catch (error) {
    console.error('Error calculating order prices:', error);
    throw {
      message: error.response?.data?.error || 'Failed to calculate order prices',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CUSTOM PRICING =====

/**
 * Create custom pricing for a distributor
 */
export const createCustomPricing = async (data) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/custom`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating custom pricing:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create custom pricing',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Deactivate custom pricing
 */
export const deactivateCustomPricing = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/custom/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deactivating custom pricing:', error);
    throw {
      message: error.response?.data?.error || 'Failed to deactivate custom pricing',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CAMPAIGNS =====

/**
 * Get active pricing campaigns
 */
export const getActiveCampaigns = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/campaigns`);
    return response.data;
  } catch (error) {
    console.error('Error fetching active campaigns:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch active campaigns',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== PRICE HISTORY =====

/**
 * Get price change history for a product
 */
export const getPriceChangeHistory = async (productId) => {
  try {
    const response = await apiClient.get(`/wholesale/products/${productId}/price-history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching price change history:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch price change history',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getPriceTiers,
  getTierById,
  updatePriceTier,
  calculateProductPrice,
  calculateOrderPrices,
  createCustomPricing,
  deactivateCustomPricing,
  getActiveCampaigns,
  getPriceChangeHistory
};
